/**
 * This Tent + That Tent — sister tent venues. Built from a shared tent
 * builder so they look like the same family.
 */

import * as THREE from 'three';
import { type StageHandle, makeScreenQuad } from './StageBase';

export interface TentOptions {
  id: string;
  label: string;
  width: number;
  depth: number;
  fabricColor: string;
  accentColor: string;
}

export class Tent {
  static build(opts: TentOptions): StageHandle {
    const group = new THREE.Group();
    group.name = opts.id;

    const matPole = new THREE.MeshStandardMaterial({
      color: '#3a3030',
      roughness: 0.5,
      metalness: 0.6
    });
    const matFabric = new THREE.MeshStandardMaterial({
      color: opts.fabricColor,
      roughness: 0.85,
      side: THREE.DoubleSide
    });
    const matDeck = new THREE.MeshStandardMaterial({
      color: '#1a1820',
      roughness: 0.85
    });
    const matScreen = new THREE.MeshBasicMaterial({ color: '#0a0a14' });
    const matAccent = new THREE.MeshStandardMaterial({
      color: opts.accentColor,
      emissive: opts.accentColor,
      emissiveIntensity: 0.3
    });

    const w = opts.width;
    const d = opts.depth;
    const ridgeH = 14;
    const eaveH = 7;

    // Stage deck at the back of the tent
    const deckW = w * 0.7;
    const deckD = d * 0.28;
    const deck = new THREE.Mesh(new THREE.BoxGeometry(deckW, 1, deckD), matDeck);
    deck.position.set(0, 0.5, -d / 2 + deckD / 2 + 1);
    deck.castShadow = true;
    deck.receiveShadow = true;
    group.add(deck);

    // Four corner poles
    for (const sx of [-1, 1] as const) {
      for (const sz of [-1, 1] as const) {
        const pole = new THREE.Mesh(
          new THREE.CylinderGeometry(0.18, 0.18, eaveH, 6),
          matPole
        );
        pole.position.set(sx * w / 2, eaveH / 2, sz * d / 2);
        pole.castShadow = true;
        group.add(pole);
      }
    }
    // Center ridge poles
    for (const sx of [-1, 1] as const) {
      const pole = new THREE.Mesh(
        new THREE.CylinderGeometry(0.22, 0.22, ridgeH, 6),
        matPole
      );
      pole.position.set(sx * w / 2 * 0.8, ridgeH / 2, 0);
      group.add(pole);
    }

    // Ridge bar
    const ridge = new THREE.Mesh(
      new THREE.BoxGeometry(w * 1.6, 0.18, 0.18),
      matPole
    );
    ridge.position.set(0, ridgeH, 0);
    group.add(ridge);

    // Fabric — two sloping rectangles connecting eave to ridge
    const fabricLen = Math.hypot(w / 2, ridgeH - eaveH);
    for (const sx of [-1, 1] as const) {
      const fabric = new THREE.Mesh(
        new THREE.PlaneGeometry(d + 1.2, fabricLen + 1.2),
        matFabric
      );
      fabric.position.set(sx * (w / 4), (eaveH + ridgeH) / 2, 0);
      fabric.rotation.y = Math.PI / 2;
      const lean = Math.atan2(ridgeH - eaveH, w / 2);
      fabric.rotation.z = sx * lean;
      group.add(fabric);
    }
    // End caps
    for (const sz of [-1, 1] as const) {
      const tri = new THREE.Mesh(
        new THREE.PlaneGeometry(w, ridgeH - eaveH + 0.4),
        matFabric
      );
      tri.position.set(0, (eaveH + ridgeH) / 2, sz * d / 2);
      if (sz < 0) tri.rotation.y = Math.PI;
      group.add(tri);
    }

    // Backdrop screen
    const screenW = w * 0.45;
    const screenH = screenW * 9 / 16;
    const screen = new THREE.Mesh(
      new THREE.PlaneGeometry(screenW, screenH),
      matScreen
    );
    screen.position.set(0, screenH / 2 + 1.4, -d / 2 + 0.55);
    group.add(screen);

    // Accent stripe along the front
    const stripe = new THREE.Mesh(
      new THREE.BoxGeometry(w, 0.18, 0.18),
      matAccent
    );
    stripe.position.set(0, eaveH + 0.1, d / 2);
    group.add(stripe);

    const audioAnchor = new THREE.Object3D();
    audioAnchor.position.set(0, 2.5, 0);
    group.add(audioAnchor);

    return {
      id: opts.id,
      label: opts.label,
      group,
      position: new THREE.Vector3(),
      audioAnchor,
      audioFalloff: { ref: 35, max: 180 },
      screenQuad: makeScreenQuad(
        new THREE.Vector3(0, screenH / 2 + 1.4, -d / 2 + 0.55),
        screenW,
        screenH,
        0
      ),
      screenWidth: screenW,
      screenHeight: screenH
    };
  }
}

export const ThisTent = {
  build: () =>
    Tent.build({
      id: 'this-tent',
      label: 'This Tent',
      width: 34,
      depth: 46,
      fabricColor: '#ddc8a8',
      accentColor: '#ff5f6d'
    })
};

export const ThatTent = {
  build: () =>
    Tent.build({
      id: 'that-tent',
      label: 'That Tent',
      width: 34,
      depth: 46,
      fabricColor: '#c8d4dd',
      accentColor: '#56c2ff'
    })
};
